import React from 'react';
import { Sparkles } from 'lucide-react';

type Tone = 'cleared' | 'held' | 'pending' | 'issued' | 'neutral';

const stampTone: Record<Tone, string> = {
  cleared: 'border-success/25 bg-success-soft text-success',
  held: 'border-danger/25 bg-danger-soft text-danger',
  pending: 'border-warning/25 bg-warning-soft text-warning',
  issued: 'border-blue-200 bg-blue-50 text-blue-700',
  neutral: 'border-rule bg-paper text-ink-2',
};

export const Stamp: React.FC<{ tone?: Tone; children: React.ReactNode; className?: string }> = ({
  tone = 'neutral',
  children,
  className = '',
}) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[5px] font-mono text-[9px] font-semibold uppercase leading-none tracking-[0.12em] ${stampTone[tone]} ${className}`}
  >
    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
    {children}
  </span>
);

export const Panel: React.FC<{
  children?: React.ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'aside';
}> = ({ children, className = '', as: Tag = 'div' }) => (
  <Tag className={`lms-panel ${className}`}>{children}</Tag>
);

export const PanelHead: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ title, action, className = '' }) => (
  <div className={`flex flex-wrap items-center justify-between gap-3 border-b border-rule px-5 py-4 ${className}`}>
    <h2 className="text-[13.5px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
    {action}
  </div>
);

export const PageHeader: React.FC<{
  title: React.ReactNode;
  lede?: React.ReactNode;
  actions?: React.ReactNode;
  eyebrow?: React.ReactNode;
  className?: string;
}> = ({ title, lede, actions, eyebrow, className = '' }) => (
  <header className={`flex flex-wrap items-end justify-between gap-x-8 gap-y-4 ${className}`}>
    <div className="min-w-0">
      {eyebrow && <p className="lms-kicker mb-2.5">{eyebrow}</p>}
      <h1 className="text-[clamp(1.85rem,2.6vw,2.5rem)] font-semibold leading-[1.08] tracking-[-0.035em] text-ink">
        {title}
      </h1>
      {lede && <p className="mt-2.5 max-w-[66ch] text-[14px] leading-6 text-ink-2">{lede}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </header>
);

export type StepState = 'done' | 'current' | 'todo';

export const CheckpointRail: React.FC<{
  steps: { label: string; state: StepState }[];
  className?: string;
  tone?: 'light' | 'dark';
  orientation?: 'horizontal' | 'vertical';
}> = ({ steps, className = '', tone = 'light', orientation = 'horizontal' }) => {
  if (steps.length === 0) return null;
  const done = steps.filter((s) => s.state === 'done').length;
  const markerClass = (state: StepState) =>
    `relative z-10 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border transition-colors ${
      state === 'done'
        ? tone === 'dark'
          ? 'border-white bg-white text-blue-700'
          : 'border-blue-600 bg-blue-600 text-white'
        : state === 'current'
          ? tone === 'dark'
            ? 'border-white bg-transparent text-white'
            : 'border-blue-600 bg-white text-blue-600'
          : tone === 'dark'
            ? 'border-white/30 bg-transparent text-transparent'
            : 'border-rule bg-white text-transparent'
    }`;
  const labelClass = (state: StepState) =>
    `font-mono text-[9px] uppercase leading-3 tracking-[0.09em] ${
      state === 'current'
        ? tone === 'dark'
          ? 'text-white'
          : 'text-blue-700'
        : state === 'done'
          ? tone === 'dark'
            ? 'text-blue-100'
            : 'text-ink'
          : tone === 'dark'
            ? 'text-blue-100/60'
            : 'text-ink-2'
    }`;
  const marker = (state: StepState) => (
    <span className={markerClass(state)}>
      {state === 'done' && (
        <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" aria-hidden="true">
          <path d="M1.5 5.2 4 7.5 8.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {state === 'current' && <span className="h-[6px] w-[6px] rounded-full bg-current" />}
    </span>
  );

  if (orientation === 'vertical') {
    return (
      <div className={className}>
        <ol className="space-y-3" role="img" aria-label={`${done} of ${steps.length} checkpoints complete`}>
          {steps.map((step, i) => (
            <li key={`${step.label}-${i}`} className="flex items-center gap-2.5">
              {marker(step.state)}
              <span className={labelClass(step.state)}>{step.label}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className="flex items-start"
        role="img"
        aria-label={`${done} of ${steps.length} checkpoints complete`}
      >
        {steps.map((step, i) => (
          <div key={`${step.label}-${i}`} className="relative flex min-w-0 flex-1 flex-col items-start">
            {i > 0 && (
              <span
                aria-hidden="true"
                className={`absolute left-0 right-1/2 top-[9px] h-px ${
                  step.state === 'done' || step.state === 'current'
                    ? tone === 'dark'
                      ? 'bg-white/55'
                      : 'bg-blue-300'
                    : tone === 'dark'
                      ? 'bg-white/20'
                      : 'bg-rule'
                }`}
              />
            )}
            {marker(step.state)}
            <span title={step.label} className={`mt-2 max-w-full truncate pr-2 ${labelClass(step.state)}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ProgressTicks: React.FC<{
  value: number;
  tone?: 'light' | 'dark';
  className?: string;
  label?: string;
}> = ({ value, tone = 'light', className = '', label }) => {
  const safe = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      className={`h-2 overflow-hidden rounded-full ${
        tone === 'dark' ? 'bg-white/20' : 'bg-paper-2'
      } ${className}`}
      role="img"
      aria-label={label ?? `${safe}% complete`}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-500 ${
          tone === 'dark'
            ? 'bg-gradient-to-r from-sky-200 to-white'
            : 'bg-gradient-to-r from-blue-600 to-sky-400'
        }`}
        style={{ width: `${safe}%` }}
      />
    </div>
  );
};

export const EmptyState: React.FC<{
  title: string;
  body: string;
  action?: React.ReactNode;
  className?: string;
}> = ({ title, body, action, className = '' }) => (
  <div className={`px-6 py-12 text-center ${className}`}>
    <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
      <Sparkles className="h-5 w-5" strokeWidth={1.6} />
    </span>
    <p className="mt-4 text-[15px] font-semibold text-ink">{title}</p>
    <p className="mx-auto mt-1.5 max-w-[46ch] text-[13px] leading-5 text-ink-2">{body}</p>
    {action && <div className="mt-5 flex justify-center">{action}</div>}
  </div>
);

export const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
  className?: string;
}> = ({ label, value, note, className = '' }) => (
  <div className={className}>
    <p className="font-mono text-[9.5px] uppercase tracking-[0.13em] text-ink-2">{label}</p>
    <p className="tnum mt-1.5 text-[30px] font-semibold leading-none tracking-[-0.04em] text-ink">{value}</p>
    {note && <p className="mt-2 text-[11.5px] leading-4 text-ink-2">{note}</p>}
  </div>
);

export const fieldCls = 'lms-field';
export const labelCls = 'lms-label';
export const thCls = 'px-4 py-3 text-left font-mono text-[9.5px] font-medium uppercase tracking-[0.12em] text-ink-2';
export const tdCls = 'px-4 py-3.5 text-[13px] text-ink';
